import type { ChauffeurPage } from "../slug-chauffeur";
import { EVENT_PRICES as P } from "./eventPricing";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const coteDAzur: ChauffeurPage = {
  slug: "cote-d-azur",
  title: "Chauffeur privé Côte d’Azur – VTC 24h/24",
  subtitle:
    "Chauffeur privé sur toute la Riviera : de Nice à Saint‑Tropez, transferts aéroport, liaisons inter‑villes et longue distance, 7j/7.",
  keywords: [
    "chauffeur privé côte d’azur",
    "vtc côte d’azur",
    "chauffeur riviera",
    "chauffeur aéroport nice riviera",
  ],
  hero: {
    title: "Chauffeur privé & VTC sur la Côte d’Azur",
    subtitle:
      "Un seul chauffeur pour toute la Riviera : aéroport de Nice, Cannes, Monaco, Antibes, Saint‑Tropez et longue distance — 24h/24, 7j/7.",
    keywords: [
      "chauffeur Riviera",
      "transfert aéroport Nice",
      "liaison inter‑villes Côte d’Azur",
      "longue distance Riviera",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC sur la Côte d’Azur",
    badges: ["Disponible 24h/24", "Toute la Riviera", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi nous choisir sur la Côte d’Azur",
    items: [
      {
        title: "Toute la Riviera, un seul chauffeur",
        description:
          "De Menton à Saint‑Tropez : un interlocuteur unique pour enchaîner les villes sans rupture de service.",
      },
      {
        title: "Longues distances maîtrisées",
        description:
          "A8, corniches et route du littoral varois : itinéraires arbitrés en direct selon le trafic et l’heure.",
      },
      {
        title: "Aéroport de Nice comme pivot",
        description:
          "2e aéroport de France au centre de la Côte : suivi de vol et desserte de toutes les destinations de la Riviera.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, chauffeurs bilingues FR/EN, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur sur la Côte d’Azur",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Depuis et vers l’aéroport de Nice Côte d’Azur (T1/T2) vers Cannes, Monaco, Antibes, Cap‑Ferrat ou Saint‑Tropez, suivi de vol inclus.",
      },
      {
        title: "Liaisons inter‑villes",
        text: "Nice ⇄ Monaco, Cannes ⇄ Monaco, Cannes ⇄ Saint‑Tropez : les axes phares de la Riviera par l’A8 ou les corniches.",
      },
      {
        title: "Tournées multi‑villes",
        text: "Journées à étapes (Èze, Villefranche, Monaco, Cap‑Ferrat) avec un parcours optimisé et le même chauffeur toute la journée.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, chauffeur dédié pour rendez‑vous d’affaires, shopping et dîners d’une ville à l’autre.",
      },
      {
        title: "Longue distance",
        text: "Au‑delà de la Côte : Marseille, Aix, Italie (Vintimille, Sanremo, Gênes, Milan) et stations de ski du Mercantour.",
      },
    ],
  },
  locations: {
    heading: "Villes & destinations desservies",
    items: [
      {
        name: "Nice",
        description: "Aéroport Côte d’Azur, Promenade des Anglais, Vieux‑Nice.",
      },
      {
        name: "Cannes",
        description: "Croisette, Palais des Festivals, Cannes‑Mandelieu.",
      },
      {
        name: "Monaco",
        description: "Monte‑Carlo, Port Hercule, héliport de Fontvieille.",
      },
      {
        name: "Antibes / Juan‑les‑Pins",
        description: "Port Vauban, Cap d’Antibes, plages de Juan.",
      },
      {
        name: "Saint‑Tropez",
        description: "Golfe, Pampelonne et port, longue distance depuis Nice.",
      },
      {
        name: "Riviera italienne",
        description: "Menton, Vintimille, Sanremo et extensions vers Milan.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour toute la Côte d’Azur",
    subheading:
      "La Riviera est un chapelet de destinations reliées par l’A8 et les corniches : un même chauffeur les enchaîne sans couture.",
    paragraphs: [
      "KS Rent Services couvre l’ensemble de la Côte d’Azur au départ de Cannes : transferts depuis l’aéroport de Nice, liaisons entre Nice, Monaco, Antibes et Saint‑Tropez, et trajets longue distance vers l’Italie ou les stations de ski. Un interlocuteur unique, pas de sous‑traitance opaque.",
      "Entre la basse corniche panoramique, la moyenne corniche et l’A8, l’itinéraire optimal dépend de l’heure et du trafic. Nos chauffeurs bilingues arbitrent en direct pour tenir vos horaires, y compris sur des journées à plusieurs villes.",
      "Pour une tournée découverte (Èze, Villefranche‑sur‑Mer, Monaco, Cap‑Ferrat) comme pour un aller longue distance vers Saint‑Tropez ou Milan, la mise à disposition garde le même véhicule et le même chauffeur d’un point à l’autre — eau, chargeurs et confort premium à bord.",
    ],
    bullets: [
      "Suivi de vol depuis l’aéroport de Nice",
      "Parcours multi‑villes optimisé",
      "Longue distance Italie & stations de ski",
    ],
  },
  process: {
    heading: "Organiser un déplacement sur la Côte d’Azur",
    steps: [
      "Communiquez vos adresses, horaires et priorités de la journée (villes, rendez‑vous, vol).",
      "Nous construisons un parcours optimisé entre les villes et validons le véhicule adapté.",
      "Votre chauffeur suit le vol, cale les heures de passage et garde une marge de sécurité.",
      "Exécution avec ajustements en direct selon le trafic, d’un arrêt au suivant.",
    ],
  },
  faq: [
    {
      q: "Quel est le prix d’un chauffeur privé sur la Côte d’Azur ?",
      a: `Nous fonctionnons en forfaits fixes définis à l’avance par trajet (aéroport de Nice ⇄ Cannes dès ${P.niceE} €, aéroport de Nice ⇄ Monaco dès ${P.monacoNiceE} €, Cannes ⇄ Monaco dès ${P.cannesMonacoE} €) et en mise à disposition pour les tournées, dès ${P.e3} € les 3 h et ${P.e8} € la journée de 8 h en Classe E. Devis immédiat pour tout autre parcours.`,
    },
    {
      q: "Pouvez‑vous enchaîner plusieurs villes dans la même journée ?",
      a: "Oui, c’est notre spécialité régionale : nous organisons des parcours multi‑étapes (Nice, Monaco, Èze, Cap‑Ferrat, Cannes…) avec un itinéraire optimisé et le même chauffeur du matin au soir.",
    },
    {
      q: "Assurez‑vous les transferts depuis l’aéroport de Nice vers toute la Riviera ?",
      a: "Oui, 24h/24 avec suivi de vol : Nice Côte d’Azur (T1/T2) vers Cannes (env. 30 min), Monaco (env. 35 min), Cap‑Ferrat (env. 30 min) ou Saint‑Tropez (env. 1h30 à 2h).",
    },
    {
      q: "Faites‑vous la longue distance au‑delà de la Côte d’Azur ?",
      a: "Oui : Marseille, Aix‑en‑Provence, la Riviera italienne (Vintimille, Sanremo, Gênes), Milan et les stations de ski, avec un véhicule confortable et un chauffeur dédié.",
    },
    {
      q: "Vos chauffeurs parlent‑ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale sur toute la Riviera.",
    },
    {
      q: "Quel véhicule pour un groupe ou une longue distance avec bagages ?",
      a: "Berline Mercedes Classe E/S pour 2 à 3 voyageurs, van premium (Class V) pour les groupes, les familles et les bagages sur longue distance, jusqu’au minibus VIP selon l’effectif.",
    },
    {
      q: "Le chauffeur reste‑t‑il disponible entre deux rendez‑vous ?",
      a: "Oui, via la mise à disposition : le même chauffeur patiente et vous reprend d’une ville ou d’un rendez‑vous à l’autre, sans nouvelle réservation.",
    },
    {
      q: "Combien de temps à l’avance faut‑il réserver ?",
      a: "Réservez au plus tôt en haute saison et pendant les grands événements (Festival de Cannes, Grand Prix de Monaco) ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
  ],
  relatedLinks: [
    {
      label: "Mariage sur la Côte d'Azur, voiture et navettes invités",
      href: "/chauffeur/voiture-mariage-cannes",
    },
    { label: "Réserver un chauffeur à Nice", href: "/chauffeur/nice" },
    { label: "Réserver un chauffeur à Monaco", href: "/chauffeur/monaco" },
    { label: "Trouver un transfert", href: "/chauffeur/transferts" },
    {
      label: "Van avec chauffeur (groupes & longue distance)",
      href: "/chauffeur/van-avec-chauffeur",
    },
  ],
  cta: {
    title: "Votre chauffeur privé sur la Côte d’Azur",
    text: "Transfert aéroport, liaison inter‑villes ou tournée multi‑villes : réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé Côte d’Azur 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC sur toute la Côte d’Azur : transferts aéroport de Nice, liaisons inter‑villes et longue distance.",
    canonical: "/chauffeur/cote-d-azur",
  },
};

export default coteDAzur;
